/**
 * Module Event Integration
 * Phase 2: System Modules Layer - Event Bus Integration Enhancement
 * 
 * Provides enhanced event handling specifically for module lifecycle and integration.
 * Extends the base EventBus with module-specific features.
 */

import { EventBus } from '../events/EventBus';
import { Events, ModuleEventData, ModuleLoadedData, ModuleLoadErrorData, ModuleUnloadedData, ModuleUnloadErrorData } from '../events/types';
import { ModuleInfo } from '../modules/types';

/**
 * Enhanced event integration for module lifecycle management
 */
export class ModuleEventIntegration {
  private eventBus: EventBus;
  private moduleEventHandlers: Map<string, Set<string>> = new Map(); // module -> handlerIds
  private moduleEventHistory: Map<string, Array<{
    event: string;
    data: any;
    timestamp: Date;
  }>> = new Map();

  constructor(eventBus?: EventBus) {
    this.eventBus = eventBus || new EventBus();
  }

  /**
   * Register module-specific event handlers
   * @param moduleName - The module name
   * @param eventHandlers - Map of event names to handler functions
   * @returns Map of event names to handler IDs
   */
  registerModuleHandlers(moduleName: string, eventHandlers: Map<string, Function>): Map<string, string> {
    const handlerIds: Map<string, string> = new Map();
    
    if (!this.moduleEventHandlers.has(moduleName)) {
      this.moduleEventHandlers.set(moduleName, new Set());
    }

    const moduleHandlerIds = this.moduleEventHandlers.get(moduleName)!;

    eventHandlers.forEach((handler, eventName) => {
      // Wrap handler with module context
      const moduleHandler = (data: any) => {
        const eventData = {
          ...data,
          moduleName,
          timestamp: new Date()
        };
        
        // Add to module event history
        this.addToModuleHistory(moduleName, eventName, eventData);
        
        try {
          handler(eventData);
        } catch (error) {
          console.error(`Error in module handler for '${moduleName}' event '${eventName}':`, error);
          this.eventBus.emit('module:event:error', {
            moduleName,
            eventName,
            error,
            data: eventData
          });
        }
      };

      const handlerId = this.eventBus.on(eventName, moduleHandler);
      handlerIds.set(eventName, handlerId);
      moduleHandlerIds.add(handlerId);
    });

    return handlerIds;
  }

  /**
   * Unregister module-specific event handlers
   * @param moduleName - The module name
   * @param eventNames - Optional array of event names to unregister, or omit to unregister all
   */
  unregisterModuleHandlers(moduleName: string, eventNames?: string[]): void {
    const moduleHandlerIds = this.moduleEventHandlers.get(moduleName);
    if (!moduleHandlerIds) return;

    if (eventNames && eventNames.length > 0) {
      // Unregister specific handlers
      eventNames.forEach(eventName => {
        // Find handler ID for this event (we'd need to track this mapping)
        // For now, we'll remove all handlers for this module
        moduleHandlerIds.forEach(handlerId => {
          this.eventBus.off(eventName, handlerId as any);
        });
      });
    } else {
      // Unregister all handlers for this module
      moduleHandlerIds.forEach(handlerId => {
        // We need to know which events these handlers were for
        // For now, we'll just clear the tracking
      });
      moduleHandlerIds.clear();
    }

    if (moduleHandlerIds.size === 0) {
      this.moduleEventHandlers.delete(moduleName);
    }
  }

  /**
   * Emit a module event with automatic lifecycle tracking
   * @param moduleName - The module name
   * @param eventName - The event name
   * @param data - The event data
   */
  emitModuleEvent(moduleName: string, eventName: string, data: any = {}): void {
    const eventData = {
      ...data,
      moduleName,
      timestamp: new Date()
    };

    // Add to module event history
    this.addToModuleHistory(moduleName, eventName, eventData);

    // Emit the event
    this.eventBus.emit(eventName, eventData);

    // Emit module-specific event for tracking
    this.eventBus.emit('module:event:emitted', {
      moduleName,
      eventName,
      data: eventData
    });
  }

  /**
   * Emit a module lifecycle event
   * @param eventType - The lifecycle event type (loaded, unloaded, error)
   * @param moduleName - The module name
   * @param data - Additional event data
   */
  emitModuleLifecycleEvent(eventType: 'loaded' | 'unloaded' | 'error', moduleName: string, data: any = {}): void {
    const baseEventData: ModuleEventData = {
      moduleName,
      timestamp: new Date(),
      ...data
    };

    switch (eventType) {
      case 'loaded':
        const loadedData: ModuleLoadedData = {
          ...baseEventData,
          module: data.module
        };
        this.eventBus.emit(Events.MODULE_LOADED, loadedData);
        break;
      
      case 'unloaded':
        const unloadedData: ModuleUnloadedData = baseEventData;
        this.eventBus.emit(Events.MODULE_UNLOADED, unloadedData);
        break;
      
      case 'error':
        const errorData: ModuleLoadErrorData | ModuleUnloadErrorData = {
          ...baseEventData,
          error: data.error || 'Unknown error'
        };
        const errorEvent = data.loadError ? Events.MODULE_LOAD_ERROR : Events.MODULE_UNLOAD_ERROR;
        this.eventBus.emit(errorEvent, errorData);
        break;
    }
  }

  /**
   * Get event history for a specific module
   * @param moduleName - The module name
   * @param limit - Maximum number of history entries to return
   * @returns Array of historical events for the module
   */
  getModuleEventHistory(moduleName: string, limit?: number): Array<{
    event: string;
    data: any;
    timestamp: Date;
  }> {
    const history = this.moduleEventHistory.get(moduleName) || [];
    return limit ? history.slice(-limit) : history;
  }

  /**
   * Get all modules that have event handlers registered
   * @returns Array of module names
   */
  getActiveModules(): string[] {
    return Array.from(this.moduleEventHandlers.keys());
  }

  /**
   * Check if a module has any event handlers registered
   * @param moduleName - The module name
   * @returns True if the module has handlers, false otherwise
   */
  hasModuleHandlers(moduleName: string): boolean {
    return this.moduleEventHandlers.has(moduleName) && 
           this.moduleEventHandlers.get(moduleName)!.size > 0;
  }

  /**
   * Get the number of event handlers for a module
   * @param moduleName - The module name
   * @returns Number of event handlers
   */
  getModuleHandlerCount(moduleName: string): number {
    return this.moduleEventHandlers.get(moduleName)?.size || 0;
  }

  /**
   * Clear all event handlers and history for a module
   * @param moduleName - The module name
   */
  clearModuleData(moduleName: string): void {
    // Unregister handlers
    this.unregisterModuleHandlers(moduleName);
    
    // Clear history
    this.moduleEventHistory.delete(moduleName);
  }

  /**
   * Clear all module data
   */
  clearAllModuleData(): void {
    this.moduleEventHandlers.clear();
    this.moduleEventHistory.clear();
  }

  /**
   * Add event to module history
   * @param moduleName - The module name
   * @param event - The event name
   * @param data - The event data
   */
  private addToModuleHistory(moduleName: string, event: string, data: any): void {
    if (!this.moduleEventHistory.has(moduleName)) {
      this.moduleEventHistory.set(moduleName, []);
    }

    const history = this.moduleEventHistory.get(moduleName)!;
    history.push({
      event,
      data,
      timestamp: new Date()
    });

    // Limit history size per module (keep last 100 events)
    if (history.length > 100) {
      history.splice(0, history.length - 100);
    }
  }

  /**
   * Create module event handlers from module manifest
   * @param moduleInfo - The module information
   * @returns Map of event names to handler functions
   */
  createModuleHandlersFromManifest(moduleInfo: ModuleInfo): Map<string, Function> {
    const handlers: Map<string, Function> = new Map();
    const { manifest, name } = moduleInfo;

    // Create handlers for module lifecycle events
    handlers.set(Events.MODULE_LOADED, (data: ModuleLoadedData) => {
      console.log(`Module ${name} loaded successfully`, data);
    });

    handlers.set(Events.MODULE_LOAD_ERROR, (data: ModuleLoadErrorData) => {
      console.error(`Module ${name} failed to load:`, data.error);
    });

    handlers.set(Events.MODULE_UNLOADED, (data: ModuleUnloadedData) => {
      console.log(`Module ${name} unloaded successfully`, data);
    });

    handlers.set(Events.MODULE_UNLOAD_ERROR, (data: ModuleUnloadErrorData) => {
      console.error(`Module ${name} failed to unload:`, data.error);
    });

    // Create handlers for module-specific events if defined in manifest
    if (manifest.events) {
      Object.keys(manifest.events).forEach((eventName: string) => {
        handlers.set(eventName, (data: any) => {
          console.log(`Event ${eventName} from module ${name}:`, data);
        });
      });
    }

    return handlers;
  }

  /**
   * Register handlers for a module based on its manifest
   * @param moduleInfo - The module information
   * @returns Map of event names to handler IDs
   */
  registerModuleFromManifest(moduleInfo: ModuleInfo): Map<string, string> {
    const handlers = this.createModuleHandlersFromManifest(moduleInfo);
    return this.registerModuleHandlers(moduleInfo.name, handlers);
  }

  /**
   * Setup module event handlers for all loaded modules
   * @param modules - Array of module information objects
   */
  setupAllModules(modules: ModuleInfo[]): void {
    modules.forEach(moduleInfo => {
      if (this.hasModuleHandlers(moduleInfo.name)) {
        // Update existing handlers
        this.unregisterModuleHandlers(moduleInfo.name);
      }
      this.registerModuleFromManifest(moduleInfo);
    });
  }
}

// Create a singleton instance
export const moduleEventIntegration = new ModuleEventIntegration();